// Schedule response write path - public.respond_to_route_assignment ONLY (Codex c138b1b).
// Direct INSERT/UPDATE/DELETE on route_assignment_responses is closed server-side and never
// attempted here. The RPC upserts, so the "Take this shift" reversal after a decline is simply
// another acknowledged call. Server enforces: own scheduled/active assignment, schedule.self
// permission, acknowledged carries no reason, declined requires one. We mirror the reason rules
// client-side so a bad combination never even leaves the device.

export type ScheduleResponseStatus = "acknowledged" | "declined";

export type DeclineReason = "sick" | "unavailable" | "schedule_conflict" | "other";

/** The four enumerated reasons - the complete set, by product decision. NO free text. */
export const DECLINE_REASONS: ReadonlyArray<{ value: DeclineReason; label: string }> = [
  { value: "sick", label: "Sick" },
  { value: "unavailable", label: "Unavailable" },
  { value: "schedule_conflict", label: "Schedule conflict" },
  { value: "other", label: "Other" },
];

export interface ScheduleResponseRpcClient {
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): Promise<{ data: unknown; error: { message: string } | null }>;
}

export type SubmitResponseResult = { ok: true } | { ok: false; message: string };

/** Fixed, SAFE failure copy - server internals never reach the officer's screen. */
const SAFE_FAILURE = "Could not send your response. Check your connection and try again.";

export async function submitScheduleResponse(
  client: ScheduleResponseRpcClient,
  input: {
    accountId: string;
    routeAssignmentId: string;
    responseStatus: ScheduleResponseStatus;
    declineReason?: DeclineReason;
  },
): Promise<SubmitResponseResult> {
  if (input.responseStatus === "declined" && input.declineReason === undefined) {
    return { ok: false, message: "Pick a reason to decline this shift." };
  }
  try {
    const { error } = await client.rpc("respond_to_route_assignment", {
      p_account_id: input.accountId,
      p_route_assignment_id: input.routeAssignmentId,
      p_response_status: input.responseStatus,
      p_decline_reason: input.responseStatus === "declined" ? (input.declineReason ?? null) : null,
    });
    if (error) {
      return { ok: false, message: SAFE_FAILURE };
    }
    return { ok: true };
  } catch {
    return { ok: false, message: SAFE_FAILURE };
  }
}
